import { useEffect, useState } from "react";
import { Check, X, Crown, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatErr, fmtDate } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";

const STATUS = { pending: ["Offen", "bg-amber-100 text-amber-700"], accepted: ["Angenommen", "bg-emerald-100 text-emerald-700"], rejected: ["Abgelehnt", "bg-slate-100 text-slate-500"] };

function Applications() {
  const [apps, setApps] = useState(null);
  const load = () => api.get("/admin/staff/applications").then(({ data }) => setApps(data)).catch(() => setApps([]));
  useEffect(() => { load(); }, []);
  const decide = async (id, action) => {
    try { await api.post(`/admin/staff/applications/${id}`, { action }); toast.success(action === "accept" ? "Angenommen – ist jetzt Moderator 🛡️" : "Abgelehnt"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  if (!apps) return <p className="text-sm text-slate-400">Lädt …</p>;
  if (!apps.length) return <p className="text-sm text-slate-500" data-testid="team-admin-no-apps">Noch keine Bewerbungen.</p>;
  return (
    <ul className="space-y-3" data-testid="team-admin-apps">
      {apps.map((a) => (
        <li key={a.id} className="rounded-2xl border border-pink-100 p-4 space-y-2" data-testid={`team-app-${a.id}`}>
          <div className="flex items-center gap-3">
            <UserAvatar userId={a.user_id} name={a.user.name} picture={a.user.picture} size="w-10 h-10" noStory />
            <div className="min-w-0 flex-1"><p className="font-bold truncate">{a.user.name}</p><p className="text-xs text-slate-400">{fmtDate(a.created_at)}</p></div>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS[a.status][1]}`}>{STATUS[a.status][0]}</span>
          </div>
          <p className="text-sm whitespace-pre-line"><b>Warum:</b> {a.motivation}</p>
          <p className="text-sm"><b>Zeit:</b> {a.hours}</p>
          {a.experience && <p className="text-sm whitespace-pre-line"><b>Erfahrung:</b> {a.experience}</p>}
          {a.status === "pending" && (
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => decide(a.id, "accept")} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-emerald-500 text-white text-sm font-bold" data-testid={`team-app-accept-${a.id}`}><Check className="w-4 h-4" /> Annehmen</button>
              <button type="button" onClick={() => decide(a.id, "reject")} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-slate-100 text-slate-600 text-sm font-bold" data-testid={`team-app-reject-${a.id}`}><X className="w-4 h-4" /> Ablehnen</button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function Leads() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const toggle = async (u, key, val) => {
    const path = key === "team_lead" ? `/admin/staff/lead/${u.user_id}` : `/admin/staff/hidden/${u.user_id}`;
    try { await api.put(path, key === "team_lead" ? { lead: val } : { hidden: val }); setUsers((l) => l.map((x) => (x.user_id === u.user_id ? { ...x, [key]: val } : x))); } catch (e) { toast.error(formatErr(e)); }
  };
  const s = q.trim().toLowerCase();
  const list = users.filter((u) => (s ? (u.name || "").toLowerCase().includes(s) : u.team_lead || u.role === "moderator" || u.role === "admin")).slice(0, 40);
  return (
    <div className="space-y-3">
      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Profil suchen, um es zur Projektleitung hinzuzufügen" className="pl-9" data-testid="team-admin-lead-search" /></div>
      <ul className="divide-y divide-pink-50">
        {list.map((u) => (
          <li key={u.user_id} className="flex items-center gap-3 py-2.5" data-testid={`team-lead-row-${u.user_id}`}>
            <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-9 h-9" noStory />
            <span className="flex-1 min-w-0 truncate text-sm font-semibold">{u.name} <span className="text-xs text-slate-400">({u.role})</span></span>
            <label className="flex flex-col items-center gap-0.5 text-[10px] font-bold text-slate-500">Leitung<Switch checked={!!u.team_lead} onCheckedChange={(v) => toggle(u, "team_lead", v)} data-testid={`team-lead-switch-${u.user_id}`} /></label>
            <label className="flex flex-col items-center gap-0.5 text-[10px] font-bold text-slate-500">Sichtbar<Switch checked={!u.team_hidden} onCheckedChange={(v) => toggle(u, "team_hidden", !v)} data-testid={`team-visible-switch-${u.user_id}`} /></label>
          </li>
        ))}
        {!list.length && <li className="py-2 text-sm text-slate-400">Keine Treffer.</li>}
      </ul>
    </div>
  );
}

export function TeamAdmin() {
  return (
    <div className="space-y-6" data-testid="team-admin">
      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4">
        <h3 className="font-display text-lg font-extrabold">Moderator-Bewerbungen</h3>
        <Applications />
      </div>
      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4">
        <h3 className="font-display text-lg font-extrabold inline-flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /> Projektleitung auf der Teamseite</h3>
        <p className="text-sm text-slate-500">„Leitung“ zeigt ein Profil oben unter „Projektleitung“. Mit „Sichtbar“ blendest du jemanden von der Teamseite aus – die Rolle bleibt dabei erhalten.</p>
        <Leads />
      </div>
    </div>
  );
}
